/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { ICache, ICacheItem, ICacheSource } from '@msdyn365-commerce/cache-internal';
import { IActionInput, IRequestContext, msdyn365Commerce, verboseLogger } from '@msdyn365-commerce/core-internal';
import { AsyncResult, DataServiceRequest, isAsyncResult } from '@msdyn365-commerce/retail-proxy';
import { IInternalTelemetry, ITelemetry } from '@msdyn365-commerce/telemetry-internal';

const GET_FROM_RETURNIDENTIFIER: string = '___GET__FROM__RETURNIDENTIIER___';

export const addLocaleInformation = (inputs: IActionInput | IActionInput[], locale: string): void => {
    const isArray = Array.isArray(inputs);
    if (isArray) {
        const actionInputs = (inputs || []) as IActionInput[];
        actionInputs.forEach(input => {
            if (input instanceof DataServiceRequest && !input.getCacheKey().endsWith(`-${locale}`)) {
                // @ts-ignore -- accessing a private class variable which is a valid JS
                const tmp = input._cacheKeyFromInput;
                // @ts-ignore -- accessing a private class variable which is a valid JS
                input._cacheKeyFromInput = `${tmp}-${locale}`;
            }
        });
    } else {
        if (inputs instanceof DataServiceRequest && !inputs.getCacheKey().endsWith(`-${locale}`)) {
            // @ts-ignore -- accessing a private class variable which is a valid JS
            const tmp = inputs._cacheKeyFromInput;
            // @ts-ignore -- accessing a private class variable which is a valid JS
            inputs._cacheKeyFromInput = `${tmp}-${locale}`;
        }
    }
};

const _resolveCacheEntryReference = (
    dataServiceRequest: DataServiceRequest,
    item: any,
    requestCache: ICache,
    appCache: ICache,
    shouldReadFromAppCache?: boolean,
    isWrappingPromise?: boolean,
    isObservable: boolean = true
) => {
    const cacheValueAsArray = (item.key as string[])
        .map(key => {
            // @ts-ignore -- accessing a private class variable which is a valid JS
            const cacheObjectType = dataServiceRequest._cacheObjectType;

            const value = requestCache.get({ typeName: cacheObjectType, key });
            if (!value && shouldReadFromAppCache && appCache) {
                appCache.copyTo({ typeName: cacheObjectType, key }, requestCache, isObservable);
            }

            if (!value || !value.item) {
                return undefined;
            }

            const valueItem: any = value.item;

            if (isAsyncResult(valueItem)) {
                return valueItem.result;
            }

            return valueItem;
        })
        .filter(data => data !== undefined && data !== null);

    if ((item.key as string[]).length > cacheValueAsArray.length) {
        return;
    }

    // If we are returning a Promise that represents a future collection, we need to unwrap it
    // @ts-ignore -- accessing a private class variable (_query) which is a valid JS
    const isReturnTypeACollection = dataServiceRequest._query.isReturnTypeACollection;

    return cacheValueAsArray && cacheValueAsArray.length > 0
        ? isReturnTypeACollection && !isWrappingPromise
            ? cacheValueAsArray
            : cacheValueAsArray[0]
        : undefined;
};

const logTelemetrySource = (
    cacheValue: ICacheItem<any>,
    dataServiceRequest: DataServiceRequest,
    cacheKey: string,
    context: IRequestContext,
    telemetry?: ITelemetry | IInternalTelemetry
) => {
    if (!telemetry) {
        return;
    }
    if (!msdyn365Commerce.isBrowser) {
        switch (cacheValue.s!) {
            case ICacheSource.AppCache: {
                // @ts-ignore -- accessing a private class variable which is a valid JS
                const cacheObjectType = dataServiceRequest._cacheObjectType || 'none';

                verboseLogger(context, telemetry, 'dataAction with {actiontype}, {cacheKey}.  source {cacheSource}', {
                    values: [cacheObjectType, cacheKey, 'AppCache']
                });
                break;
            }
            case ICacheSource.RequestCache: {
                // @ts-ignore -- accessing a private class variable which is a valid JS
                const cacheObjectType = dataServiceRequest._cacheObjectType || 'none';

                verboseLogger(context, telemetry, 'dataAction with {actiontype}, {cacheKey}.  source {cacheSource}', {
                    values: [cacheObjectType, cacheKey, 'RequestCache']
                });
                break;
            }
            default: {
                // @ts-ignore -- accessing a private class variable which is a valid JS
                const cacheObjectType = dataServiceRequest._cacheObjectType || 'none';

                verboseLogger(context, telemetry, 'dataAction with {actiontype}, {cacheKey}.  source {cacheSource}', {
                    values: [cacheObjectType, cacheKey, 'Default']
                });
                break;
            }
        }
    }
};

const readCacheEntriesBasedOnCacheKeyFromReturnType = (
    dataServiceRequest: DataServiceRequest,
    requestCache: ICache,
    appCache: ICache,
    shouldReadFromAppCache: boolean,
    context: IRequestContext,
    telemetry?: ITelemetry | IInternalTelemetry,
    isObservable: boolean = true
): any => {
    // @ts-ignore -- accessing a private class variable which is a valid JS
    const cacheKeyFromReturnType = dataServiceRequest._cacheKeyFromReturnType;

    // @ts-ignore -- accessing a private class variable which is a valid JS
    const cacheKeyIdentifierFromReturnType = dataServiceRequest._cacheKeyIdentifierFromReturnType;

    // @ts-ignore -- accessing a private class variable which is a valid JS
    const cacheObjectType = dataServiceRequest._cacheObjectType;

    if (cacheKeyFromReturnType.length === 0) {
        return { entry: null, shouldRefresh: false };
    }
    let shouldRefreshAll = true;
    const cacheEntries = cacheKeyFromReturnType
        .map((entry: any) => {
            const key = `${cacheKeyIdentifierFromReturnType}-${entry}`;

            let cacheValue = requestCache.get({ typeName: cacheObjectType, key });

            if (!cacheValue && shouldReadFromAppCache && appCache) {
                cacheValue = appCache.copyTo({ typeName: cacheObjectType, key }, requestCache, isObservable);
            }

            if (!cacheValue || !cacheValue.item) {
                return null;
            }
            // according to the logic, will treat the entries to shouldRefresh when all entries have shouldRefresh.
            if (!cacheValue.shouldRefresh) {
                shouldRefreshAll = false;
            }
            logTelemetrySource(cacheValue, dataServiceRequest, key, context, telemetry);

            return cacheValue.item;
        })
        .filter(Boolean);

    // @ts-ignore -- accessing a private class variable (_query) which is a valid JS
    const isReturnTypeACollection = dataServiceRequest._query.isReturnTypeACollection;
    const cacheEntriesReturn = cacheEntries && cacheEntries.length > 0 ? (isReturnTypeACollection ? cacheEntries : cacheEntries[0]) : null;
    return { entry: cacheEntriesReturn, shouldRefresh: shouldRefreshAll };
};

export const readCacheEntries = (
    dataServiceRequest: DataServiceRequest,
    requestCache: ICache,
    appCache: ICache,
    shouldReadFromAppCache: boolean = false,
    context: IRequestContext,
    telemetry?: ITelemetry | IInternalTelemetry,
    isObservable: boolean = true
): any => {
    // @ts-ignore -- accessing a private class variable which is a valid JS
    const cacheKeyFromReturnType = dataServiceRequest._cacheKeyFromReturnType;
    // @ts-ignore -- accessing a private class variable which is a valid JS
    const cacheObjectType = dataServiceRequest._cacheObjectType;

    if (!requestCache || (!dataServiceRequest.getCacheKey() && cacheKeyFromReturnType.length === 0) || !cacheObjectType) {
        return { entry: null, shouldRefresh: false };
    }

    // @ts-ignore -- accessing a private class variable which is a valid JS
    const cacheKeyFromInput = dataServiceRequest._cacheKeyFromInput;
    // @ts-ignore -- accessing a private class variable which is a valid JS
    const cacheKeyIdentifierFromReturnType = dataServiceRequest._cacheKeyIdentifierFromReturnType;

    if (cacheKeyFromInput === GET_FROM_RETURNIDENTIFIER || (cacheKeyIdentifierFromReturnType && cacheKeyFromReturnType.length !== 0)) {
        const entries = readCacheEntriesBasedOnCacheKeyFromReturnType(
            dataServiceRequest,
            requestCache,
            appCache,
            shouldReadFromAppCache,
            context,
            telemetry,
            isObservable
        );
        if (entries && entries.entry) {
            return entries;
        }
    }
    const cacheKey = { typeName: cacheObjectType, key: cacheKeyFromInput };

    let cachedValueBasedOnInput = requestCache.get(cacheKey);
    if (cachedValueBasedOnInput) {
        cachedValueBasedOnInput = { ...cachedValueBasedOnInput };
    } else if (shouldReadFromAppCache && appCache) {
        cachedValueBasedOnInput = appCache.copyTo(cacheKey, requestCache, isObservable);
    }

    if (!cachedValueBasedOnInput) {
        return readCacheEntriesBasedOnCacheKeyFromReturnType(
            dataServiceRequest,
            requestCache,
            appCache,
            shouldReadFromAppCache,
            context,
            telemetry,
            isObservable
        );
    }
    logTelemetrySource(cachedValueBasedOnInput, dataServiceRequest, cacheKeyFromInput, context, telemetry);

    if (cachedValueBasedOnInput.item instanceof AsyncResult) {
        if (cachedValueBasedOnInput.item.status !== 'LOADING') {
            cachedValueBasedOnInput.item = cachedValueBasedOnInput.item.result || cachedValueBasedOnInput.item.error;
        } else {
            const asyncResult = cachedValueBasedOnInput.item.then(result => {
                if (typeof result !== 'object' || result.$ref !== true) {
                    return result;
                }

                return _resolveCacheEntryReference(
                    dataServiceRequest,
                    result,
                    requestCache,
                    appCache,
                    shouldReadFromAppCache,
                    isObservable
                );
            });
            return { entry: asyncResult, shouldRefresh: cachedValueBasedOnInput.shouldRefresh };
        }
    }

    if (
        typeof cachedValueBasedOnInput.item !== 'object' ||
        (cachedValueBasedOnInput.item &&
            // @ts-ignore -- $ref property does not exists in type object
            cachedValueBasedOnInput.item.$ref !== true)
    ) {
        return { entry: cachedValueBasedOnInput.item, shouldRefresh: cachedValueBasedOnInput.shouldRefresh };
    }

    const cachedValueBasedOnInputItem: any = cachedValueBasedOnInput.item;

    const resolvedRef = _resolveCacheEntryReference(
        dataServiceRequest,
        cachedValueBasedOnInput.item,
        requestCache,
        appCache,
        shouldReadFromAppCache,
        isAsyncResult(cachedValueBasedOnInputItem),
        isObservable
    );
    return { entry: resolvedRef, shouldRefresh: cachedValueBasedOnInput.shouldRefresh };
};

const _saveEntityToCache = (
    dataServiceRequest: DataServiceRequest,
    data: any,
    requestCache: ICache,
    appCache: ICache,
    isCacheTypeApplication: boolean,
    isInstance: boolean = false
): { [cacheKey: string]: any } | undefined => {
    // @ts-ignore -- accessing a private class variable which is a valid JS
    const cacheKeyFromInput = dataServiceRequest._cacheKeyFromInput;
    // @ts-ignore -- accessing a private class variable which is a valid JS
    const cacheKeyIdentifierFromReturnType = dataServiceRequest._cacheKeyIdentifierFromReturnType;
    // @ts-ignore -- accessing a private class variable which is a valid JS
    const dataServiceRequestQuery = dataServiceRequest._query;
    // @ts-ignore -- accessing a private class variable which is a valid JS
    const cacheObjectType = dataServiceRequest._cacheObjectType;

    if (!data || (!cacheKeyFromInput && !cacheKeyIdentifierFromReturnType)) {
        return;
    }

    const cacheEntries: any = {};
    const results = data;

    // using Object.prototype instead of Array.isArray because results can be Observable Array which can't be detected with Array.isArray
    if (!dataServiceRequestQuery.isReturnTypeACollection || Object.prototype.toString.call(results) !== '[object Array]') {
        if (cacheKeyIdentifierFromReturnType) {
            const key = `${cacheKeyIdentifierFromReturnType}-${results[cacheKeyIdentifierFromReturnType]}`;
            cacheEntries[key] = results;
            if (cacheKeyFromInput) {
                let existingCacheItem = requestCache.get({
                    typeName: cacheObjectType,
                    key: cacheKeyFromInput
                });
                if (existingCacheItem) {
                    existingCacheItem = { ...existingCacheItem };
                }

                if (
                    existingCacheItem &&
                    existingCacheItem.item &&
                    (!(existingCacheItem.item instanceof AsyncResult) || existingCacheItem.item.status === 'SUCCESS') &&
                    (typeof existingCacheItem.item !== 'object' ||
                        (existingCacheItem &&
                            existingCacheItem.item &&
                            // @ts-ignore -- $ref property does not exists in type object
                            existingCacheItem?.item?.$ref !== true))
                ) {
                    cacheEntries[cacheKeyFromInput] = results;
                } else {
                    cacheEntries[cacheKeyFromInput] = {
                        $ref: true,
                        type: cacheObjectType,
                        identifier: cacheKeyIdentifierFromReturnType,
                        key: [key],
                        // @ts-ignore -- accessing a private class variable which is a valid JS
                        count: dataServiceRequest._totalCount
                    };
                }
            }
        } else {
            cacheEntries[cacheKeyFromInput] = results;
        }
    } else if (!cacheKeyIdentifierFromReturnType) {
        cacheEntries[cacheKeyFromInput] = results;
    } else {
        const cacheValue = results
            .map((result: { [x: string]: any }) => {
                const identifierValue = result[cacheKeyIdentifierFromReturnType];
                if (identifierValue) {
                    const key = `${cacheKeyIdentifierFromReturnType}-${identifierValue}`;
                    cacheEntries[key] = result;
                    return key;
                }
            })
            .filter(Boolean);
        let existingCacheItem = requestCache.get({
            typeName: cacheObjectType,
            key: cacheKeyFromInput
        });
        if (existingCacheItem) {
            existingCacheItem = { ...existingCacheItem };
        }
        if (
            existingCacheItem &&
            existingCacheItem.item &&
            (!(existingCacheItem.item instanceof AsyncResult) || existingCacheItem.item.status === 'SUCCESS') &&
            (typeof existingCacheItem.item !== 'object' ||
                (existingCacheItem &&
                    existingCacheItem.item &&
                    // @ts-ignore -- $ref property does not exists in type object
                    existingCacheItem.item.$ref !== true))
        ) {
            cacheEntries[cacheKeyFromInput] = results;
        } else {
            if (existingCacheItem) {
                // @ts-ignore - _cacheObjectType does not exist in type ICacheItem
                const existingCacheObjectType = existingCacheItem._cacheObjectType;
                // @ts-ignore - _cacheKeyIdentifierFromReturnType does not exist in type ICacheItem
                const existingCacheKeyIdentifierFromReturnType = existingCacheItem._cacheKeyIdentifierFromReturnType;
                // @ts-ignore - _query does not exist in type ICacheItem
                const existingCacheIsReturnTypeACollection = existingCacheItem._query.isReturnTypeACollection;
                // @ts-ignore - _totalCount does not exist in type ICacheItem
                const existingCacheTotalCount = existingCacheItem._totalCount;

                cacheEntries[cacheKeyFromInput] = {
                    $ref: true,
                    type: existingCacheObjectType,
                    identifier: existingCacheKeyIdentifierFromReturnType,
                    isCollection: existingCacheIsReturnTypeACollection,
                    key: cacheValue,
                    count: existingCacheTotalCount
                };
            }
        }
    }

    if (cacheEntries) {
        Object.keys(cacheEntries).map(entryKey => {
            requestCache.put(
                { typeName: cacheObjectType, key: entryKey },
                { item: cacheEntries[entryKey], ...(isInstance && { instance: 'instance' }) }
            );

            if (appCache && isCacheTypeApplication) {
                appCache.put(
                    { typeName: cacheObjectType, key: entryKey },
                    { item: cacheEntries[entryKey], ...(isInstance && { instance: 'instance' }) }
                );
            }
        });
    }

    return cacheEntries;
};

export const saveCacheEntries = (
    dataServiceRequest: DataServiceRequest,
    data: AsyncResult<any> | any,
    requestCache: ICache,
    appCache: ICache,
    isCacheTypeApplication: boolean,
    isInstance: boolean = false
): { [cacheKey: string]: any } | undefined => {
    // @ts-ignore -- accessing a private class variable which is a valid JS
    const cacheKeyFromInput = dataServiceRequest._cacheKeyFromInput;
    // @ts-ignore -- accessing a private class variable which is a valid JS
    const cacheKeyIdentifierFromReturnType = dataServiceRequest._cacheKeyIdentifierFromReturnType;
    // @ts-ignore -- accessing a private class variable which is a valid JS
    const cacheObjectType = dataServiceRequest._cacheObjectType;

    // if return Entity is collection
    //        if entity type has identifier/primary key
    //              1. cache array of primay key value of returned entity collection on input cache key
    //              2. cache individual entity on primary key
    //        else
    //              1. cache output on input cache key
    //
    // else  if entity type has identifier/primary key
    //           2. cache output entity on primary key
    if (!data || (!cacheKeyFromInput && !cacheKeyIdentifierFromReturnType) || !cacheObjectType) {
        return;
    }

    if (data instanceof AsyncResult) {
        // For a loading Promise, cache based on input cachekey, and then cache output again once complete
        if (data.status === 'LOADING') {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            data.then((result: any) => {
                // @ts-ignore -- accessing a private class variable which is a valid JS
                if (dataServiceRequest._totalCount !== undefined) {
                    // @ts-ignore -- accessing a private class variable which is a valid JS
                    data.metadata.count = dataServiceRequest._totalCount;
                }
                // @ts-ignore -- accessing a private class variable which is a valid JS
                if (dataServiceRequest && dataServiceRequest._otherOdata) {
                    // @ts-ignore -- accessing a private class variable which is a valid JS
                    data.metadata.others = dataServiceRequest._otherOdata;
                }

                _saveEntityToCache(dataServiceRequest, result, requestCache, appCache, isCacheTypeApplication, isInstance);
                return result;
            });
            requestCache.put(
                { typeName: cacheObjectType, key: cacheKeyFromInput },
                { item: data, ...(isInstance && { instance: 'instance' }) }
            );

            if (appCache && isCacheTypeApplication) {
                appCache.put(
                    { typeName: cacheObjectType, key: cacheKeyFromInput },
                    { item: data, ...(isInstance && { instance: 'instance' }) }
                );
            }
            // For a completed promise, save the action results to the cache
        } else {
            // @ts-ignore -- accessing a private class variable which is a valid JS
            if (dataServiceRequest._totalCount !== undefined) {
                // @ts-ignore -- accessing a private class variable which is a valid JS
                data.metadata.count = dataServiceRequest._totalCount;
            }
            // @ts-ignore -- accessing a private class variable which is a valid JS
            if (dataServiceRequest && dataServiceRequest._otherOdata) {
                // @ts-ignore -- accessing a private class variable which is a valid JS
                data.metadata.others = dataServiceRequest._otherOdata;
            }
            _saveEntityToCache(dataServiceRequest, data.result, requestCache, appCache, isCacheTypeApplication, isInstance);
            return data.result;
        }
    } else {
        return _saveEntityToCache(dataServiceRequest, data, requestCache, appCache, isCacheTypeApplication, isInstance);
    }
};
